// src/pages/seller/Seller2Net/components/PropertyDetailsSection.js
import React, { useCallback, useEffect, useMemo } from "react";
import FloatingInput from "../../../../components/input/FloatingInput";
import FloatingNumber from "../../../../components/input/FloatingNumber";
import { parseCurrency } from "../utils/buyerNet.shared";
import { computeConventionalLoanAmount } from "../utils/calculations";

const RadioGroup = ({ name, value, onChange, options }) => (
  <div className="frm-radio">
    {options.map((opt) => (
      <label key={opt.value}>
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
        />{" "}
        {opt.label}
      </label>
    ))}
  </div>
);
/*
const CommissionInput = ({ label, value, onChange }) => (
  <div className="frm-field-c">
    <label className="heading-d">{label}:</label>
    <div className="form-group sign-of-percentage">
      <FloatingNumber
        type="text"
        placeholder="0.00"
        className="inpt-pla"
        value={value}
        onChange={(e) => onChange(e?.target?.value ?? "")}
      />
    </div>
  </div>
);  */

function PropertyDetailsSection({
  ttbData,
  formatCurrency,
  isCorrectProperty,
  setIsCorrectProperty,
  isInitialLoad,
  useAnnualPropertyTax,
  setUseAnnualPropertyTax,
  salesPriceError,
  useHomeWarranty, // unused but kept for compatibility
  formData,
  setFormData,
  setValue,
  handleCalculate,
  templateformData,
}) {
  // Memoize derived values for cleaner render
  const taxAmount = useMemo(() => Number(ttbData?.taxAmount || 0), [ttbData]);

  // ✅ Keep loanInfoMeta.annualPropertyTax in sync with the property's fetched tax
  // amount + the user's "Do you want to use this amount?" answer. Without this,
  // annualPropertyTax can be left stale/unset if it was applied before a later
  // company-template reload, or if the user changes their Yes/No answer after
  // the fact — neither of which re-applies the value on their own.
  useEffect(() => {
    if (!isCorrectProperty) return;
    const rawTaxAmount = ttbData?.taxAmount;
    if (rawTaxAmount === undefined || rawTaxAmount === null || rawTaxAmount === "") {
      return;
    }

    const desired = useAnnualPropertyTax ? rawTaxAmount : "0.00";

    // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
    // taxDebugLog("PropertyDetailsSection sync effect", {
    //   rawTaxAmount,
    //   useAnnualPropertyTax,
    //   desired,
    //   isCorrectProperty,
    // });

    setFormData((prev) => {
      if (
        prev?.loanInfoMeta?.annualPropertyTax === desired &&
        prev?.loanInfoMeta?.annualPropertyTaxAmount === rawTaxAmount
      ) {
        return prev;
      }
      return {
        ...prev,
        loanInfoMeta: {
          ...(prev?.loanInfoMeta || {}),
          annualPropertyTaxAmount: rawTaxAmount,
          annualPropertyTax: desired,
        },
      };
    });

    // Keep react-hook-form's own value in sync too — otherwise it stays whatever
    // the last useUserTemplate reset() stamped it as (often "0.00", applied before
    // any address existed), and ManualSheetSection's RHF->formData sync effect will
    // later overwrite the correct plain-state value above with that stale one.
    if (typeof setValue === "function") {
      setValue("loanInfoMeta.annualPropertyTax", desired, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("loanInfoMeta.annualPropertyTaxAmount", rawTaxAmount, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [
    ttbData?.taxAmount,
    useAnnualPropertyTax,
    isCorrectProperty,
    setFormData,
    setValue,
  ]);

  // Intentionally does NOT commit into formData.salesPrice on every
  // keystroke. FloatingNumber keeps its own internal display state for
  // live typing, but formData.salesPrice is watched directly by the
  // auto-populate loan-amount effect below and by useBuyerNetEngine's
  // automatic recalculation effect — committing here made the whole
  // Calculation (escrow/title data, PMI, etc.) refire on every digit
  // typed instead of once. Manual Sheet avoids this the same way: its
  // salesPrice field (ControlledField) only calls back into formData
  // from onBlur, never onChange. handleSalesPriceBlur below is the
  // actual commit point.
  const handleSalesPriceChange = useCallback(() => {}, []);

  const handleSalesPriceBlur = useCallback(
    (e) => {
      const raw = e?.target?.value ?? "";
      setFormData((p) => ({ ...p, salesPrice: raw }));
    },
    [setFormData]
  );

  // Drives the real loanInfoMeta.buyersLoanType so the engine's own "Cash" branch
  // (getTitleEscrowData in useBuyerNetEngine.js) takes over — zeroing loan amount,
  // PMI, and P&I — instead of layering a separate cash flag on top of it. Mirrors
  // the same buyerClosingCostsMeta/monthsTaxes bookkeeping the Manual Sheet's own
  // Loan Type dropdown does when switching to/from "Cash" (ManualSheetSection.js),
  // so the two entry points to the same field stay consistent.
  const handleCashTransactionChange = useCallback(
    (val) =>
      setFormData((p) => {
        const prevLoan = p.loanInfoMeta || {};
        const prevPrepaids = p.prepaidsMeta || {};
        const wasCash = prevLoan.buyersLoanType === "Cash";

        let prepaidsMetaUpdate = {};
        if (val && !wasCash) {
          prepaidsMetaUpdate = {
            monthsTaxesBeforeCash: prevPrepaids.monthsTaxes,
            monthsTaxes: "0",
          };
        } else if (!val && wasCash) {
          prepaidsMetaUpdate = {
            monthsTaxes: prevPrepaids.monthsTaxesBeforeCash ?? prevPrepaids.monthsTaxes,
          };
        }

        const selectedMeta = val
          ? templateformData?.cash || []
          : templateformData?.conventional || [];

        return {
          ...p,
          isCashTransaction: val,
          ...(val ? { loanAmount: "" } : {}),
          // Switching back to a financed transaction should get a fresh
          // LTV-based suggestion rather than staying stuck on whatever the
          // user manually typed the last time this question was "No" (or
          // being permanently skipped because it's still marked manual from
          // then) — the auto-populate effect below re-fires once this is
          // false again and a Sales Price already exists.
          ...(!val ? { loanAmountManuallySet: false } : {}),
          loanInfoMeta: {
            ...prevLoan,
            buyersLoanType: val ? "Cash" : "Conventional",
            requestedDownPayment: "0.00",
          },
          prepaidsMeta: {
            ...prevPrepaids,
            ...prepaidsMetaUpdate,
          },
          buyerClosingCostsMeta: selectedMeta,
        };
      }),
    [setFormData, templateformData]
  );

  const handleLoanAmountChange = useCallback(
    (e) =>
      setFormData((p) => ({
        ...p,
        loanAmount: e.target.value,
        // Marks this field as deliberately overridden so the auto-populate
        // effect below never overwrites it again — matches how manual
        // overrides are treated everywhere else in this app (e.g. the
        // Origination Fee / Manual Sheet closing-cost fields).
        loanAmountManuallySet: true,
      })),
    [setFormData]
  );

  // Guided's own version of Manual Sheet's admin-configured LTV default:
  // once a valid Sales Price exists (and this isn't a Cash transaction),
  // suggest a loan amount using the exact same formula and the exact same
  // admin-configured conventionalPercent Manual Sheet's getAmountConventional
  // uses (see computeConventionalLoanAmount in utils/calculations.js) —
  // e.g. $700,000 at a 90% LTV default suggests $630,000. Recomputes on
  // every Sales Price change until the user manually edits the field
  // (handleLoanAmountChange above), at which point their entry always wins.
  // Also derives requestedDownPayment/buyerDownPayment immediately, the
  // same way handleLoanAmountBlur does for a manual entry — otherwise the
  // down payment and other loan-dependent results would stay at their
  // stale/zero defaults until the user happened to blur the field
  // themselves, even though a real suggested amount is already showing.
  useEffect(() => {
    if (formData.isCashTransaction) return;
    if (formData.loanAmountManuallySet) return;

    const salesPrice = parseCurrency(formData.salesPrice);
    const conventionalPercent = parseCurrency(
      formData?.loanInfoMeta?.conventionalPercent,
    );
    if (!salesPrice || !conventionalPercent) return;

    const suggestedLoanAmount = computeConventionalLoanAmount(
      salesPrice,
      conventionalPercent,
    );
    if (formData.loanAmount === suggestedLoanAmount) return;

    const downPayment = Math.max(
      salesPrice - parseCurrency(suggestedLoanAmount),
      0,
    ).toFixed(2);

    // console.log("[loanAmountFlow] auto-populate effect (Guided)", {
    //   salesPrice,
    //   conventionalPercent,
    //   previousLoanAmount: formData.loanAmount,
    //   suggestedLoanAmount,
    //   downPayment,
    // });

    setFormData((p) => {
      if (p.loanAmount === suggestedLoanAmount) return p;
      return {
        ...p,
        loanAmount: suggestedLoanAmount,
        loanInfoMeta: {
          ...p.loanInfoMeta,
          requestedDownPayment: downPayment,
          buyerDownPayment: downPayment,
        },
      };
    });

    // Keep react-hook-form's own store in sync synchronously here too —
    // otherwise there's a window (between this update and
    // useBuyerNetEngine's separate formData->RHF loanInfoMeta sync effect
    // catching up a render later) where blurring a field right after typing
    // reads RHF's still-stale requestedDownPayment via useFormSync's
    // getValues() snapshot, which useFormSync then writes back over
    // formData wholesale — corrupting conventionalPercent through
    // getAmountConventional's requestedDownPayment-override branch.
    if (typeof setValue === "function") {
      setValue("loanAmount", suggestedLoanAmount, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("loanInfoMeta.requestedDownPayment", downPayment, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("loanInfoMeta.buyerDownPayment", downPayment, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [
    formData.salesPrice,
    formData.loanAmount,
    formData?.loanInfoMeta?.conventionalPercent,
    formData.isCashTransaction,
    formData.loanAmountManuallySet,
    setFormData,
    setValue,
  ]);

  // The loan amount is what the user actually cares about staying fixed, so we
  // derive the down payment from it (salesPrice - loanAmount) and store that as
  // loanInfoMeta.requestedDownPayment — the same override field the Manual Sheet's
  // "Down Payment" input already writes to (see getAmountConventional in
  // utils/calculations.js). useBuyerNetEngine's main recalculation effect watches
  // this field, so committing it here is enough to trigger a fresh calculation.
  const handleLoanAmountBlur = useCallback(() => {
    setFormData((p) => {
      const salesPrice = parseCurrency(p.salesPrice);
      const loanAmount = parseCurrency(p.loanAmount);
      if (!salesPrice || !loanAmount) return p;

      const downPayment = Math.max(salesPrice - loanAmount, 0).toFixed(2);
      return {
        ...p,
        loanInfoMeta: {
          ...p.loanInfoMeta,
          requestedDownPayment: downPayment,
          buyerDownPayment: downPayment,
        },
      };
    });
  }, [setFormData]);

  const handleDateChange = useCallback(
    (e) =>
      setFormData((p) => ({
        ...p,
        loanInfoMeta: {
          ...p.loanInfoMeta,
          estimatedClosingDate: e.target.value,
        },
      })),
    [setFormData]
  );

  // if (!isInitialLoad) return null;

  return (
    <div className="property-detail-container">
      <div className="property-details-list">
        {/* ✅ Correct property question */}
        <div className="property-question">
          <div className="content-description">
            <p className="heading-d txtclr">Is this the correct property? </p>
          </div>
          <div className="form-value">
            <RadioGroup
              name="correctproperty"
              value={isCorrectProperty}
              onChange={setIsCorrectProperty}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
            />
          </div>
        </div>

        {isCorrectProperty && (
          <>
            {/* ✅ Annual Property Tax */}
            {taxAmount > 0 && (
              <>
                <div className="property-question p-t-20 m-t-20 border-top-sec">
                  <div className="content-description">
                    <p className="heading-f txtclr">
                      We see the annual Property Tax on this property is{" "}
                      <button>${formatCurrency(ttbData.taxAmount)}</button> which
                      was the bill in {ttbData.taxYear}.
                    </p>
                  </div>
                </div>

                <div className="property-question m-t-20">
                  <div className="content-description">
                    <p className="heading-b txtclr">
                      Do you want to use this amount in the calculations?
                    </p>
                  </div>
                  <div className="form-value">
                    <RadioGroup
                      name="annualPropertyTaxUse"
                      value={useAnnualPropertyTax}
                      onChange={(val) => setUseAnnualPropertyTax(val === true)}
                      options={[
                        { label: "Yes", value: true },
                        { label: "No", value: false },
                      ]}
                    />
                  </div>
                </div>
              </>
            )}

            {/* ✅ Sales Price */}
            <div className="property-question p-t-20 m-t-20 border-top-sec">
              <div className="content-description">
                <p className="heading-f txtclr">Sales price details</p>
              </div>
            </div>

            <div className="property-question m-t-20">
              <div className="content-description">
                <p className="heading-b txtclr">
                  What amount do you want to use in the calculations?
                </p>
              </div>
              <div className="form-value">
                <div className="form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="salesPrice"
                    placeholder="0.00"
                    className="inpt-pla"
                    value={formData.salesPrice || ""}
                    onChange={handleSalesPriceChange}
                    onBlur={handleSalesPriceBlur}
                  />
                  {salesPriceError && (
                    <div className="text-red-600 text-sm mt-1">
                      {salesPriceError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ✅ Cash transaction */}
            <div className="property-question p-t-20 m-t-20 border-top-sec">
              <div className="content-description">
                <p className="heading-b txtclr">
                  Is this a Cash transaction?
                </p>
              </div>
              <div className="form-value">
                <RadioGroup
                  name="isCashTransaction"
                  value={formData.isCashTransaction ?? false}
                  onChange={handleCashTransactionChange}
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                />
              </div>
            </div>

            {!(formData.isCashTransaction ?? false) && (
              <div className="property-question m-t-20">
                <div className="content-description">
                  <p className="heading-b txtclr">
                    What is the loan amount?
                  </p>
                </div>
                <div className="form-value">
                  <div className="form-group sign-of-amount">
                    <FloatingNumber
                      type="text"
                      name="loanAmount"
                      placeholder="0.00"
                      className="inpt-pla"
                      value={formData.loanAmount || "0.00"}
                      onChange={handleLoanAmountChange}
                      onBlur={handleLoanAmountBlur}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ✅ Agent Commission Split
            <div className="property-question p-t-20 m-t-20 border-top-sec">
              <div className="content-description">
                <p className="heading-f txtclr">Agent Commission Split</p>
              </div>
            </div>

            <div className="property-question m-t-20">
              <div className="lable-wrap">
                <CommissionInput
                  label={sellerItem?.label || "Seller Broker Compensation"}
                  value={
                    sellerItem?.rate ??
                    formData?.compensationMeta?.sellerBrokerCompensation ??
                    ""
                  }
                  onChange={handleSellerCompChange}
                />
                <CommissionInput
                  label={buyerItem?.label || "Buyer Broker Compensation"}
                  value={
                    buyerItem?.rate ??
                    formData?.compensationMeta?.buyerBrokerCompensation ??
                    ""
                  }
                  onChange={handleBuyerCompChange}
                />
              </div>
            </div>*/}

            {/* ✅ Closing date */}
            <div className="property-question p-t-20 m-t-20 border-top-sec">
              <div className="content-description">
                <p className="heading-f txtclr">Closing date</p>
              </div>
            </div>

            <div className="property-question m-t-20">
              <div className="content-description">
                <p className="heading-b txtclr">
                  What is the Estimated Closing Date:
                </p>
              </div>
              <div className="form-value">
                <div className="form-group">
                  <FloatingInput
                    type="date"
                    name="estimatedClosingDate"
                    label=""
                    placeholder="MM/DD/YYYY"
                    className="inpt-pla"
                    value={formData.loanInfoMeta.estimatedClosingDate || ""}
                    onChange={handleDateChange}
                  />
                </div>
              </div>
            </div>

            {/* ✅ Calculate Button */}
            <div className="middle-content-calc calculate-btn">
              <button
                className="submit-btn btn-bg inpt-btn"
                onClick={handleCalculate}
              >
                Calculate
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default React.memo(PropertyDetailsSection);
